import React, {
  memo, useEffect, useRef, useState,
} from 'react';

import {
  TILE_CSS_H,
  TILE_CSS_W,
  colorForBox,
  colorForLabel,
  GLOBAL_IMAGE_CONCURRENCY,
  tileBoxesKey,
  withGlobalGate,
} from '../helpers';
import { fetchAssetBlob } from '../signedAssetCache';
import style from './style.module.scss';
import { useMapView } from './Map';

const tileKeyOf = (tile) => `${tile.row}-${tile.col}-${tile.index}`;

const sheetSize = (cols, rows, cell) => ({
  w: cols * cell.w,
  h: rows * cell.h,
  cellW: cell.w,
  cellH: cell.h,
});

/**
 * Batch stroke by label color — one beginPath/stroke per color.
 * Returns box count painted.
 */
const paintDetectionFrames = (ctx, list, cellW, cellH, tileBoxes, selectedLabels, prefix) => {
  if (!selectedLabels?.size || !tileBoxes) return 0;
  const stroke = Math.max(2, Math.min(cellW, cellH) * 0.006);
  const byColor = new Map();

  list.forEach((tile) => {
    const boxes = tileBoxes[tileBoxesKey(tile, prefix)] || [];
    if (!boxes.length) return;
    const dx = (Number(tile.col) || 0) * cellW;
    const dy = (Number(tile.row) || 0) * cellH;
    boxes.forEach((box) => {
      if (box?.BoundingBox && selectedLabels.has(box.label)) {
        const {
          Left = 0, Top = 0, Width = 0, Height = 0,
        } = box.BoundingBox;
        const bw = Width * cellW;
        const bh = Height * cellH;
        if (bw >= 0.5 && bh >= 0.5) {
          const color = colorForBox(box);
          let flat = byColor.get(color);
          if (!flat) {
            flat = [];
            byColor.set(color, flat);
          }
          flat.push(dx + Left * cellW, dy + Top * cellH, bw, bh);
        }
      }
    });
  });

  let n = 0;
  byColor.forEach((flat, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = stroke;
    ctx.beginPath();
    for (let i = 0; i < flat.length; i += 4) {
      ctx.rect(flat[i], flat[i + 1], flat[i + 2], flat[i + 3]);
      n += 1;
    }
    ctx.stroke();
  });
  return n;
};

/**
 * Paint seats onto a dedicated layer (Figma-style).
 * Uses mosaic CSS coords (xPx/yPx) → atlas pixels. No image dependency.
 * Toggle = CSS visibility on this canvas — never re-run this for show/hide.
 */
const paintSrsLayer = (ctx, w, h, cellW, cellH, overlay, scanId) => {
  ctx.clearRect(0, 0, w, h);
  const seats = (scanId && overlay?.byScan?.[scanId]) || overlay?.seats;
  if (!seats?.length || cellW <= 0 || cellH <= 0) return 0;

  const kx = cellW / TILE_CSS_W;
  const ky = cellH / TILE_CSS_H;
  const stroke = Math.max(1, Math.min(cellW, cellH) * 0.004);

  const byColor = new Map();
  seats.forEach((s) => {
    if (scanId && s.scanId && s.scanId !== scanId) return;
    const color = colorForLabel(s.section || '?');
    let flat = byColor.get(color);
    if (!flat) {
      flat = [];
      byColor.set(color, flat);
    }
    const side = Math.max(1, (s.sizePx || 8) * kx);
    const cx = (Number(s.xPx) || 0) * kx;
    const cy = (Number(s.yPx) || 0) * ky;
    flat.push(cx - side / 2, cy - side / 2, side, side);
  });

  let n = 0;
  byColor.forEach((flat, color) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = stroke;
    ctx.beginPath();
    for (let i = 0; i < flat.length; i += 4) {
      ctx.rect(flat[i], flat[i + 1], flat[i + 2], flat[i + 3]);
      n += 1;
    }
    ctx.stroke();
  });
  return n;
};

const closeBitmap = (bmp) => {
  if (bmp && typeof bmp.close === 'function') {
    try { bmp.close(); } catch { /* already closed */ }
  }
};

const loadImageFromBlob = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('img error'));
  };
  img.src = url;
});

const loadTileBitmap = (url) => (
  withGlobalGate('crowd-count-images', GLOBAL_IMAGE_CONCURRENCY, async () => {
    const blob = await fetchAssetBlob(url);
    if (!blob) throw new Error('no blob');
    if (typeof createImageBitmap !== 'function') return loadImageFromBlob(blob);
    return createImageBitmap(blob);
  })
);

const loadOneTile = async (tile) => {
  const preview = tile?.webp || tile?.url;
  if (!preview) return null;
  try {
    return await loadTileBitmap(preview);
  } catch {
    if (tile?.url && tile.url !== preview) {
      try { return await loadTileBitmap(tile.url); } catch { /* full also failed */ }
    }
    return null;
  }
};

/**
 * Two layers: base (tiles/boxes) + SRS (pre-baked seats).
 * SRS toggle is CSS visibility — compositor only, no React paint work.
 *
 * The first tile sets the cell to its size times qualityMultiplier. Zoom scales that bitmap.
 */
const MosaicCanvas = ({
  tiles,
  rows,
  cols,
  loading = false,
  tileBoxes = null,
  selectedLabels = null,
  boxKeyPrefix = null,
  /** Clears image cache only when this changes (selected scan/option). */
  scanKey = '',
  viewportLoad = true,
  overlay = null,
  bakeSrs = true,
  onSettled = null,
}) => {
  const canvasRef = useRef(null);
  const boxesCanvasRef = useRef(null);
  const srsCanvasRef = useRef(null);
  const paintedRef = useRef(new Set());
  const failedRef = useRef(new Set());
  const inflightRef = useRef(new Set());
  const settledRef = useRef(false);
  const genRef = useRef(0);
  const cellRef = useRef(null);
  const [cell, setCell] = useState(null);
  const [paintedCount, setPaintedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [wanted, setWanted] = useState(() => new Set());
  const { viewportEl } = useMapView();

  const list = tiles || [];
  const r = Number(rows) || 0;
  const c = Number(cols) || 0;
  const scanId = boxKeyPrefix || null;
  const listRef = useRef(list);
  listRef.current = list;

  useEffect(() => {
    genRef.current += 1;
    paintedRef.current = new Set();
    failedRef.current = new Set();
    inflightRef.current = new Set();
    settledRef.current = false;
    cellRef.current = null;
    setCell(null);
    setWanted(new Set());
    setPaintedCount(0);
    setFailedCount(0);
    return () => { genRef.current += 1; };
  }, [scanKey]);

  // Single-region: request every tile. Do not depend on viewportEl (it mounts async
  // and was re-firing this effect, canceling in-flight loads → permanent gaps).
  useEffect(() => {
    if (viewportLoad || loading || !list.length || r < 1 || c < 1) return;
    const all = new Set(list.map(tileKeyOf));
    setWanted((prev) => {
      if (prev.size === all.size) {
        let same = true;
        all.forEach((k) => { if (!prev.has(k)) same = false; });
        if (same) return prev;
      }
      return all;
    });
  }, [viewportLoad, loading, scanKey, list.length, r, c]);

  // Stadium: grow wanted as sentinels intersect the map viewport.
  useEffect(() => {
    if (!viewportLoad || loading || !list.length || r < 1 || c < 1) return undefined;
    const root = canvasRef.current?.parentElement;
    if (!root) return undefined;

    let alive = true;
    const io = new IntersectionObserver(
      (entries) => {
        if (!alive) return;
        const hit = [];
        entries.forEach((entry) => {
          const key = entry.target.dataset.loadKey;
          if (!key || !entry.isIntersecting) return;
          hit.push(key);
        });
        if (!hit.length) return;
        setWanted((prev) => {
          let changed = false;
          const next = new Set(prev);
          hit.forEach((key) => {
            if (!next.has(key)) {
              next.add(key);
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      },
      { root: viewportEl || null, rootMargin: '30%', threshold: 0 },
    );

    root.querySelectorAll('[data-load-key]').forEach((el) => io.observe(el));
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [viewportLoad, loading, scanKey, list.length, r, c, viewportEl]);

  useEffect(() => {
    if (loading || !wanted.size || r < 1 || c < 1) return;
    const gen = genRef.current;

    wanted.forEach((key) => {
      if (
        paintedRef.current.has(key)
        || failedRef.current.has(key)
        || inflightRef.current.has(key)
      ) return;
      const tile = listRef.current.find((t) => tileKeyOf(t) === key);
      if (!tile) {
        failedRef.current.add(key);
        setFailedCount(failedRef.current.size);
        return;
      }
      inflightRef.current.add(key);
      loadOneTile(tile).then((img) => {
        inflightRef.current.delete(key);
        if (gen !== genRef.current) {
          closeBitmap(img);
          return;
        }
        const canvas = canvasRef.current;
        if (!canvas || !img) {
          closeBitmap(img);
          failedRef.current.add(key);
          setFailedCount(failedRef.current.size);
          return;
        }
        if (!cellRef.current) {
          // GPU max texture edge. Full WebP when the sheet fits; otherwise scale to this.
          const gl = document.createElement('canvas').getContext('webgl');
          const maxEdge = gl?.getParameter(gl.MAX_TEXTURE_SIZE) || 16384;
          gl?.getExtension('WEBGL_lose_context')?.loseContext();
          const qualityMultiplier = Math.min(
            1,
            maxEdge / (c * img.width),
            maxEdge / (r * img.height),
          );
          console.log(`Canvas max texture edge is ${maxEdge}px. Preview quality multiplier is ${qualityMultiplier}.`);

          cellRef.current = {
            w: Math.max(1, Math.round(img.width * qualityMultiplier)),
            h: Math.max(1, Math.round(img.height * qualityMultiplier)),
          };
          setCell(cellRef.current);
          const { w, h } = sheetSize(c, r, cellRef.current);
          canvas.width = w;
          canvas.height = h;
          const base = canvas.getContext('2d', { alpha: false });
          if (base) {
            base.fillStyle = '#1a1d22';
            base.fillRect(0, 0, w, h);
          }
        }
        const { cellW, cellH } = sheetSize(c, r, cellRef.current);
        const ctx = canvas.getContext('2d', { alpha: false });
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(
            img,
            (Number(tile.col) || 0) * cellW,
            (Number(tile.row) || 0) * cellH,
            cellW,
            cellH,
          );
        }
        closeBitmap(img);
        paintedRef.current.add(key);
        setPaintedCount(paintedRef.current.size);
      }).catch(() => {
        inflightRef.current.delete(key);
        if (gen !== genRef.current) return;
        failedRef.current.add(key);
        setFailedCount(failedRef.current.size);
      });
    });
  }, [wanted, loading, scanKey, r, c]);

  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (loading || settledRef.current) return;
    const notify = onSettledRef.current;
    if (!notify) return;
    if (!list.length) {
      settledRef.current = true;
      notify();
      return;
    }
    if (inflightRef.current.size > 0) return;
    if (wanted.size < list.length) return;
    const done = paintedRef.current.size + failedRef.current.size;
    if (done < wanted.size) return;
    settledRef.current = true;
    notify();
  }, [loading, list.length, wanted, paintedCount, failedCount]);

  const needBoxes = Boolean(selectedLabels?.size);
  const needSrs = Boolean(bakeSrs && overlay?.seats?.length);

  // Boxes layer: selection change / detections — never redraws mosaic images.
  // Visibility is CSS [data-boxes-show]; this always keeps the bitmap ready.
  useEffect(() => {
    const canvas = boxesCanvasRef.current;
    if (!canvas || loading || !cell || r < 1 || c < 1 || !needBoxes) return;
    const {
      w, h, cellW, cellH,
    } = sheetSize(c, r, cell);

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);
    paintDetectionFrames(
      ctx,
      list,
      cellW,
      cellH,
      tileBoxes,
      selectedLabels,
      boxKeyPrefix,
    );
  }, [
    loading, list, r, c, cell, tileBoxes, selectedLabels, boxKeyPrefix, needBoxes,
  ]);

  // SRS layer: bake once when overlay / atlas size ready. Toggle does not touch this.
  useEffect(() => {
    const canvas = srsCanvasRef.current;
    if (!canvas || loading || !cell || r < 1 || c < 1 || !needSrs) return;
    const {
      w, h, cellW, cellH,
    } = sheetSize(c, r, cell);

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    paintSrsLayer(ctx, w, h, cellW, cellH, overlay, scanId);
  }, [loading, overlay, scanId, r, c, cell, needSrs]);

  const hasAnyImage = paintedCount > 0;
  const showLoading = loading || (!hasAnyImage && list.length > 0);

  return (
    <div className={style.mosaicLayer} aria-hidden={false}>
      <canvas
        ref={canvasRef}
        className={style.mosaicCanvas}
      />
      <div className={style.decorativeCanvases} aria-hidden="true">
        {needBoxes ? (
          <canvas
            ref={boxesCanvasRef}
            className={style.boxesCanvas}
          />
        ) : null}
        {needSrs ? (
          <canvas
            ref={srsCanvasRef}
            className={style.srsCanvas}
          />
        ) : null}
      </div>
      {viewportLoad && !loading && list.length > 0 ? (
        <div
          className={style.loadSentinels}
          style={{
            gridTemplateColumns: `repeat(${c}, ${TILE_CSS_W}px)`,
            gridTemplateRows: `repeat(${r}, ${TILE_CSS_H}px)`,
          }}
        >
          {list.map((tile) => {
            const key = tileKeyOf(tile);
            return (
              <div
                key={boxKeyPrefix ? `${boxKeyPrefix}:${key}` : key}
                data-load-key={key}
                className={style.loadSentinel}
              />
            );
          })}
        </div>
      ) : null}
      {showLoading ? <div className={style.mosaicLoading} /> : null}
    </div>
  );
};

export default memo(MosaicCanvas);
export {
  paintDetectionFrames,
  paintSrsLayer,
};
